import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import type { ReactNode } from 'react';
import api from '@/services/api';
import type {
  User,
  LoginCredentials,
  ChangePasswordData,
} from '@/types';
import toast from 'react-hot-toast';
import { AuthService } from '@/services/authService';

interface AuthContextType {
  user: User | null;
  token: string | null;
  refreshToken: string | null;
  login: (credentials: LoginCredentials) => Promise<void>;
  register: (username: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  changePassword: (data: ChangePasswordData) => Promise<void>;
  checkTokenValidity: () => Promise<boolean>;
  refreshAuthToken: () => Promise<boolean>;
  updateUser: (userData: Partial<User>) => void;
  isLoading: boolean;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

// Utility per rilevare errori di connessione
const isConnectionError = (error: any): boolean => {
  const connectionErrors = [
    'ERR_CONNECTION_REFUSED',
    'ECONNREFUSED',
    'ERR_NETWORK',
    'ERR_TIMEOUT',
    'NETWORK_ERROR'
  ];

  return connectionErrors.some(errCode =>
    error?.code === errCode ||
    error?.message?.includes(errCode) ||
    error?.message?.includes('Network Error') ||
    error?.message?.includes('timeout')
  );
};

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [refreshToken, setRefreshToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const isAuthenticated = !!user && !!token && !!refreshToken;

  // Pulisce tutti i dati auth
  const clearAuthData = useCallback(() => {
    console.log('🧹 Clearing auth data...');
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
    setToken(null);
    setRefreshToken(null);
    setUser(null);
  }, []);

  // Verifica validità token interno con retry
  const checkTokenValidityInternal = async (tokenToCheck: string): Promise<boolean> => {
    try {
      const response = await api.post('/auth/verify-token', {}, {
        headers: {
          'Authorization': `Bearer ${tokenToCheck}`,
          'Content-Type': 'application/json',
        },
      });

      return response.status === 200 && response.data.success && response.data.data.valid === true;
    } catch (error: any) {
      // Distingui tra errori di connessione e errori di autenticazione
      if (isConnectionError(error)) {
        console.log('🔄 Connection error, server might be restarting...');
        throw new Error('CONNECTION_ERROR');
      }

      console.log('❌ Backend says token is invalid:', error.response?.status);
      return false;
    }
  };

  // Refresh access token usando refresh token
  const refreshAccessToken = useCallback(async (): Promise<boolean> => {
    try {
      const storedRefreshToken = localStorage.getItem('refreshToken');

      if (!storedRefreshToken) {
        console.log('❌ No refresh token available');
        return false;
      }

      console.log('🔄 Requesting token refresh from backend...');

      const response = await AuthService.refreshToken(storedRefreshToken);

      console.log('✅ Backend provided new token');

      // Aggiorna token memorizzati
      localStorage.setItem('token', response.token);
      localStorage.setItem('refreshToken', response.refreshToken);
      localStorage.setItem('user', JSON.stringify(response.user));

      // Aggiorna state
      setToken(response.token);
      setRefreshToken(response.refreshToken);
      setUser(response.user);

      return true;

    } catch (error: any) {
      // Distingui tra errori di connessione e errori reali
      if (isConnectionError(error)) {
        console.log('🔄 Connection error during refresh, server might be restarting...');
        throw new Error('CONNECTION_ERROR');
      }

      console.error('❌ Backend refresh failed:', error.response?.data?.message || error.message);
      return false;
    }
  }, []);

  // Retry con backoff esponenziale
  const retryWithBackoff = async (
    fn: () => Promise<any>,
    maxRetries: number = 3,
    baseDelay: number = 1000
  ): Promise<any> => {
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        return await fn();
      } catch (error: any) {
        if (!isConnectionError(error) || attempt === maxRetries) {
          throw error;
        }

        const delay = baseDelay * Math.pow(2, attempt - 1);
        console.log(`🔄 Retry attempt ${attempt}/${maxRetries} in ${delay}ms...`);
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
  };

  // Ascolta cambi esterni di localStorage
  useEffect(() => {
    const handleStorageChange = () => {
      const storedToken = localStorage.getItem('token');
      const storedRefreshToken = localStorage.getItem('refreshToken');
      const storedUser = localStorage.getItem('user');

      // Se localStorage è stato pulito esternamente, aggiorna lo state
      if (!storedToken && isAuthenticated) {
        console.log('🔍 Auth cleared externally, updating state...');
        setToken(null);
        setRefreshToken(null);
        setUser(null);

        if (!window.location.pathname.includes('/login')) {
          window.location.href = '/login';
        }
      }
      // Se il token è stato aggiornato esternamente, sincronizza lo state
      else if (storedToken && storedToken !== token && storedUser && storedRefreshToken) {
        console.log('🔄 Token updated externally, syncing state...');
        const userData = JSON.parse(storedUser);
        setToken(storedToken);
        setRefreshToken(storedRefreshToken);
        setUser(userData);
      }
    };

    // Ascolta cambi di storage e controlla ogni secondo
    window.addEventListener('storage', handleStorageChange);
    const interval = setInterval(handleStorageChange, 1000);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(interval);
    };
  }, [token, refreshToken, isAuthenticated]);

  // Inizializza stato utente da localStorage al mount
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        const storedToken = localStorage.getItem('token');
        const storedRefreshToken = localStorage.getItem('refreshToken');
        const storedUser = localStorage.getItem('user');

        // Se NON ci sono dati memorizzati, completa l'inizializzazione immediatamente
        if (!storedToken || !storedRefreshToken || !storedUser) {
          console.log('📭 No stored auth data found');
          setIsLoading(false);
          return;
        }

        const userData = JSON.parse(storedUser);

        try {
          // Usa retry per gestire i riavvii del server
          const isValid = await retryWithBackoff(
            () => checkTokenValidityInternal(storedToken),
            3,
            2000
          );

          if (isValid) {
            setToken(storedToken);
            setRefreshToken(storedRefreshToken);
            setUser(userData);
          } else {
            console.log('❌ Backend says stored token is invalid, trying refresh...');

            try {
              const refreshSuccess = await retryWithBackoff(
                () => refreshAccessToken(),
                3,
                2000
              );

              if (!refreshSuccess) {
                console.log('❌ Refresh failed, clearing auth data');
                clearAuthData();
              }
            } catch (refreshError) {
              console.log('❌ Refresh failed after retries, clearing auth data');
              clearAuthData();
            }
          }
        } catch (connectionError) {
          // Se dopo tutti i retry non riesce a connettersi,
          // mantieni i dati locali e riprova più tardi
          console.log('🔄 Could not connect to server, keeping local auth data');
          setToken(storedToken);
          setRefreshToken(storedRefreshToken);
          setUser(userData);

          // Riprova tra 10 secondi
          setTimeout(() => {
            console.log('🔄 Retrying auth check...');
            initializeAuth();
          }, 10000);
        }
      } catch (error) {
        console.error('❌ Error initializing auth:', error);
        clearAuthData();
      } finally {
        setIsLoading(false);
      }
    };

    initializeAuth();
  }, [clearAuthData, refreshAccessToken]);

  // Funzione di login
  const login = useCallback(async (credentials: LoginCredentials): Promise<void> => {
    try {
      setIsLoading(true);
      const response = await AuthService.login(credentials);

      if (!response.token || !response.refreshToken || !response.user) {
        throw new Error('Invalid login response format');
      }

      console.log('✅ Processing login...');

      // Memorizza in localStorage
      localStorage.setItem('token', response.token);
      localStorage.setItem('refreshToken', response.refreshToken);
      localStorage.setItem('user', JSON.stringify(response.user));

      // Aggiorna state
      setToken(response.token);
      setRefreshToken(response.refreshToken);
      setUser(response.user);

      toast.success('Login successful!');
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Errore nel login');
      console.error('❌ Error during login:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Funzione di register
  const register = useCallback(async (username: string, password: string): Promise<void> => {
    try {
      setIsLoading(true);
      const response = await AuthService.register({ username, password });

      if (!response.token || !response.refreshToken || !response.user) {
        throw new Error('Invalid register response format');
      }

      console.log('✅ Processing registration...');

      // Memorizza in localStorage
      localStorage.setItem('token', response.token);
      localStorage.setItem('refreshToken', response.refreshToken);
      localStorage.setItem('user', JSON.stringify(response.user));

      // Aggiorna state
      setToken(response.token);
      setRefreshToken(response.refreshToken);
      setUser(response.user);

      toast.success('Registration successful!');
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Errore nella registrazione');
      console.error('❌ Error during registration:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Funzione di logout
  const logout = useCallback(async (): Promise<void> => {
    try {
      console.log('🚪 Logging out...');
      await AuthService.logout();
    } catch (error: any) {
      console.error('Error during logout API call:', error);
    } finally {
      clearAuthData();
      window.location.href = '/';
      toast.success('Logged out successfully');
    }
  }, [clearAuthData]);

  // Verifica validità token pubblica
  const checkTokenValidity = async (): Promise<boolean> => {
    if (!token) {
      return false;
    }

    try {
      const isValid = await retryWithBackoff(
        () => checkTokenValidityInternal(token),
        2,
        1000
      );

      if (!isValid) {
        console.log('🔄 Backend says token invalid, attempting refresh...');

        try {
          const refreshSuccess = await retryWithBackoff(
            () => refreshAccessToken(),
            2,
            1000
          );
          return refreshSuccess;
        } catch (refreshError) {
          toast.error('❌ Errore durante il refresh del token');
          console.log('❌ Refresh failed after retries');
          return false;
        }
      }

      return true;
    } catch (error) {
      if (isConnectionError(error)) {
        console.log('🔄 Connection error, assuming token is still valid locally');
        return true;
      }

      console.error('Error checking token validity:', error);
      return false;
    }
  };

  // Funzione per refresh manuale del token
  const refreshAuthToken = useCallback(async (): Promise<boolean> => {
    try {
      return await refreshAccessToken();
    } catch (error) {
      console.error('Manual token refresh failed:', error);
      return false;
    }
  }, [refreshAccessToken]);

  // Funzione cambio password
  const changePassword = useCallback(async (data: ChangePasswordData): Promise<void> => {
    try {
      await AuthService.changePassword(data);
    } catch (error) {
      throw error;
    }
  }, []);

  // Aggiorna user
  const updateUser = useCallback((userData: Partial<User>): void => {
    if (user) {
      const updatedUser = { ...user, ...userData };
      setUser(updatedUser);
      localStorage.setItem('user', JSON.stringify(updatedUser));
    }
  }, [user]);

  // Context value
  const contextValue: AuthContextType = {
    user,
    token,
    refreshToken,
    login,
    register,
    logout,
    changePassword,
    checkTokenValidity,
    refreshAuthToken,
    updateUser,
    isLoading,
    isAuthenticated,
  };

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};